<script lang="ts">
  import Tooltip from "@shared/svelte/Components/Tooltip.svelte";
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import SettingsPageWrapper from "@modern/components/retailer/settings/PageWrapper.svelte";
  import type { RetailerUser } from "@shared/types/User";
  import { updateProfileApi, updatePasswordApi } from "./UserApi.svelte";
  import Flash from "@shared/services/flashv2.js";

  export let user: RetailerUser;
  let newEmail: string = user.email;
  let newPhone: string = String(user.phone);
  let newFirstName: string = user.first_name;
  let newLastName: string = user.last_name;
  let submittingProfile: boolean;

  const updateProfile = async function (userInfo) {
    submittingProfile = true;

    updateProfileApi(userInfo)
      .then(res => {
        submittingProfile = false;
        user.email = userInfo.email;
        user.first_name = userInfo.first_name;
        user.last_name = userInfo.last_name;
        user.phone = userInfo.phone;
        Object.assign(window["$rootScope"].user, user);
        Flash.now({ type: "success", message: "We've updated your information!" });
      })
      .catch(res => {
        submittingProfile = false;
        let errors = res.join("<br>");
        Flash.now({
          type: "warning",
          rawHtml: true,
          message: "There was an error updating your information.<br>" + errors,
        });
      });
  };

  let newPass: string = "";
  let newPassConfirm: string = "";
  let submittingPassword: boolean;
  const updatePassword: (accountInfo: any) => void = async function (accountInfo) {
    submittingPassword = true;

    let resp = await updatePasswordApi(accountInfo);
    if (resp.ok) {
      submittingPassword = false;
      newPass = "";
      newPassConfirm = "";
      Flash.now({ type: "success", message: "We've updated your password!" });
    } else {
      submittingPassword = false;
      if (resp.status >= 400 && resp.status < 500) {
        let json = await resp.json();
        let showWeakPasswordMessage = json?.errors?.messages?.some(m => m.match(/weak/));

        if (showWeakPasswordMessage) {
          Flash.now({
            type: "warning",
            rawHtml: true,
            message:
              "There was an error updating your password.<br><br> \
            Passwords must be at least 8 characters and contain a letter, number, and valid special character.<br><br> \
            Please try again or contact support.",
          });
        } else {
          Flash.now({
            type: "warning",
            rawHtml: true,
            message: `There was an error updating your password.<br><br> \
               ${json.errors.messages.join(", ")}<br><br> \
               Please try again or contact support.`,
          });
        }
      } else {
        alert("There was a server error when updating your information.");
        console.error(resp);
      }
    }
  };

  let toolTipText = "We'll only text you critical order updates such as order rejections.";
  $: cleanedNumber = newPhone;

  $: disableProfileButton =
    submittingProfile ||
    (newEmail == user.email &&
      newFirstName == user.first_name &&
      newLastName == user.last_name &&
      newPhone == String(user.phone));

  $: disableAccountButton = submittingPassword || !newPass.length || newPass != newPassConfirm;

  $: userObject = {
    email: newEmail,
    phone: cleanedNumber,
    first_name: newFirstName,
    last_name: newLastName,
  };

  $: accountObject = {
    newPass: newPass,
    newPassConfirm: newPassConfirm,
  };
</script>

<SettingsPageWrapper>
  <!-- User info -->
  <div class="padding--small fizz-stack-32">
    <div class="fizz-heading-3 fizz-stack-24">Profile Information</div>
    <div class="fizz-grid fizz-stack-24">
      <div class="fizz-grid-row-full">
        <div class="fizz-stack-8">
          <label class="fizz-label" for="profile-email">Email Address</label>
        </div>
        <input
          type="text"
          name="input-text"
          id="profile-email"
          class="fizz-input-text fizz-full-width retailer-settings-email"
          bind:value={newEmail}
          disabled={submittingProfile}
        />
      </div>

      <div class="fizz-grid-row-full">
        <div class="fizz-flex-row-4 fizz-stack-8">
          <label class="fizz-label" for="profile-phone">Cell Phone</label>
          <Tooltip text={toolTipText} />
        </div>
        <PhoneInput bind:value={newPhone} id={"profile-phone"} disabled={submittingProfile} />
      </div>
    </div>

    <div class="fizz-grid fizz-stack-32">
      <div class="fizz-grid-row-full">
        <div class="fizz-stack-8">
          <label class="fizz-label" for="input-text">First Name</label>
        </div>
        <input
          type="text"
          name="input-text"
          id="profile-first-name"
          class="fizz-input-text fizz-full-width retailer-settings-first-name"
          bind:value={newFirstName}
          disabled={submittingProfile}
        />
      </div>

      <div class="fizz-grid-row-full">
        <div class="fizz-stack-8">
          <label class="fizz-label" for="input-text" style="margin-right: 0.25rem;">Last Name</label
          >
        </div>
        <input
          type="text"
          name="input-text"
          id="profile-last-name"
          class="fizz-input-text fizz-full-width retailer-settings-last-name"
          bind:value={newLastName}
          disabled={submittingProfile}
        />
      </div>
    </div>

    <div class="margin-top--small">
      <button
        class="fizz-button-primary"
        disabled={disableProfileButton}
        on:click={() => updateProfile(userObject)}>Update</button
      >
    </div>
  </div>

  <!-- AccountObject info -->
  <div class="padding--small">
    <div class="fizz-heading-3">Change Password</div>
    <p class="fizz-text-subdued fizz-stack-24">
      Password must be at least 8 characters, contain letters, numbers and special characters (!, @,
      #, $, %, ^, &, *, _)
    </p>
    <div class="fizz-grid fizz-stack-24">
      <div class="fizz-text-left fizz-inline-16">
        <div class="fizz-stack-8">
          <label class="fizz-label" for="input-text">New Password</label>
        </div>
        <input
          type="password"
          name="input-text"
          id="profile-pass"
          class="fizz-input-text retailer-settings-pass"
          bind:value={newPass}
          disabled={submittingPassword}
        />
      </div>
    </div>

    <div class="fizz-grid fizz-stack-24">
      <div class="fizz-text-left fizz-inline-16">
        <div class="fizz-stack-8">
          <label class="fizz-label" for="input-text" style="margin-right: 0.25rem;"
            >Confirm New Password</label
          >
        </div>
        <input
          type="password"
          name="input-text"
          id="profile-confirm-pass"
          class="fizz-input-text retailer-settings-pass confirm"
          bind:value={newPassConfirm}
          disabled={submittingPassword}
        />
      </div>
    </div>

    <div class="margin-top--small">
      <button
        class="fizz-button-primary"
        disabled={disableAccountButton}
        on:click={() => updatePassword(accountObject)}>Change Password</button
      >
    </div>
  </div>
</SettingsPageWrapper>
